import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthContext';
import axios from 'axios';
import { API_CONFIG } from '../../config/api';

const PlanManagement = () => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [changingPlan, setChangingPlan] = useState(false);

  const { getJwtToken } = useAuth();

  // Available plans
  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      textQuota: 100,
      audioQuota: 50,
      features: [
        '100 text descriptions per month',
        '50 audio descriptions per month',
        '20+ languages supported',
        '50+ voice options',
        'Standard support',
        '99% uptime SLA'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 10,
      textQuota: 500,
      audioQuota: 250,
      features: [
        '500 text descriptions per month',
        '250 audio descriptions per month',
        'Additional credits in $10 increments',
        'Priority support',
        '99.9% uptime SLA',
        'Usage analytics'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 100,
      textQuota: 10000,
      audioQuota: 5000,
      features: [
        '10,000 text descriptions per month',
        '5,000 audio descriptions per month',
        'Custom integrations',
        'Dedicated support team',
        '99.99% uptime SLA',
        'Advanced analytics'
      ]
    }
  ];

  // Fetch user's current plan on component mount
  useEffect(() => {
    fetchUserPlan();
  }, []);

  // Fetch user's current plan
  const fetchUserPlan = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let token;
      try {
        token = await getJwtToken();
      } catch (tokenErr) {
        console.error('Error getting JWT token:', tokenErr);
        setError('Authentication error. Please try signing out and signing in again.');
        setLoading(false);
        return;
      }
      
      // Removed excessive logging
      const response = await axios.get(`${API_CONFIG.DASHBOARD_API_URL}/plan`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Removed excessive logging
      setPlan(response.data.plan);
    } catch (err) {
      console.error('Error fetching user plan:', err);
      
      // Detailed error logging
      if (err.response) {
        console.error('Error response status:', err.response.status);
        setError(`Failed to fetch plan: ${err.response.status} ${JSON.stringify(err.response.data)}`);
      } else if (err.request) {
        console.error('Error request:', err.request);
        setError('Failed to fetch plan: No response received from server. CORS issue or network error.');
      } else {
        console.error('Error message:', err.message);
        setError(`Failed to fetch plan: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Update user's plan
  const updatePlan = async (planId) => {
    if (!window.confirm(`Are you sure you want to switch to the ${planId.charAt(0).toUpperCase() + planId.slice(1)} plan?`)) {
      return;
    }
    
    try {
      setChangingPlan(true);
      setError(null);
      
      let token;
      try {
        token = await getJwtToken();
      } catch (tokenErr) {
        console.error('Error getting JWT token:', tokenErr);
        setError('Authentication error. Please try signing out and signing in again.');
        setChangingPlan(false);
        return;
      }
      
      // Reduce excessive logging
      // console.log('Making update plan request to:', `${API_CONFIG.DASHBOARD_API_URL}/plan`);
      
      const response = await axios.put(
        `${API_CONFIG.DASHBOARD_API_URL}/plan`,
        { plan_id: planId },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Removed excessive logging
      setPlan(response.data.plan);
      setSuccessMessage(`Successfully updated to ${planId.charAt(0).toUpperCase() + planId.slice(1)} plan!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error updating plan:', err);
      
      // Detailed error logging
      if (err.response) {
        // Log only essential error information
        console.error('Error response status:', err.response.status);
        setError(`Failed to update plan: ${err.response.status} ${JSON.stringify(err.response.data)}`);
      } else if (err.request) {
        console.error('Error request:', err.request);
        setError('Failed to update plan: No response received from server. CORS issue or network error.');
      } else {
        console.error('Error message:', err.message);
        setError(`Failed to update plan: ${err.message}`);
      }
    } finally {
      setChangingPlan(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-6">Subscription Plan</h2>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          <p className="font-bold">Error:</p>
          <p>{error}</p>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}
      
      {loading ? (
        <div className="text-center py-4">Loading plan information...</div>
      ) : (
        <>
          {/* Current Plan */}
          {plan && (
            <div className="mb-8 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Current Plan</h3>
              <p className="text-2xl font-bold text-blue-600 mb-2">
                {plan.name || plan.plan_id.charAt(0).toUpperCase() + plan.plan_id.slice(1)}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-700"><strong>Text Quota:</strong> {plan.text_quota} requests/month</p>
                  <p className="text-gray-700"><strong>Audio Quota:</strong> {plan.audio_quota} requests/month</p>
                </div>
                <div>
                  <p className="text-gray-700"><strong>Renewal Date:</strong> {plan.renewal_date ? new Date(plan.renewal_date).toLocaleDateString() : 'N/A'}</p>
                  <p className="text-gray-700"><strong>Price:</strong> ${plan.price || 0}/month</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Available Plans */}
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Available Plans</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((planOption) => (
              <div 
                key={planOption.id} 
                className={`border rounded-lg p-4 ${plan && plan.plan_id === planOption.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
              >
                <h4 className="text-xl font-bold mb-2">{planOption.name}</h4>
                <p className="text-2xl font-bold text-gray-800 mb-4">
                  ${planOption.price}<span className="text-sm font-normal text-gray-600">/month</span>
                </p>
                <ul className="mb-6 space-y-2">
                  {planOption.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => updatePlan(planOption.id)}
                  disabled={changingPlan || (plan && plan.plan_id === planOption.id)}
                  className={`w-full py-2 px-4 rounded ${
                    plan && plan.plan_id === planOption.id
                      ? 'bg-gray-300 text-gray-700 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  } disabled:opacity-50`}
                >
                  {plan && plan.plan_id === planOption.id ? 'Current Plan' : 'Select Plan'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
      
      {/* Enterprise Contact */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Need a Custom Plan?</h3>
        <p className="text-gray-600 mb-4">
          Contact our sales team for custom enterprise solutions with higher quotas, dedicated support, and custom integrations.
        </p>
        <a 
          href="mailto:sales@describeapi.com" 
          className="inline-block bg-gray-800 hover:bg-gray-900 text-white py-2 px-4 rounded"
        >
          Contact Sales
        </a>
      </div>
    </div>
  );
};

export default PlanManagement;
